import * as Toggle from '@radix-ui/react-toggle-group'
import { useKestrel } from 'features/kestrelViewer'
import { AnnotationEntry } from 'features/kestrelViewer/kestrelViewerMarkup/annotations/AnnotationEntry'
import { CameraViewEntry } from 'features/kestrelViewer/kestrelViewerMarkup/cameraViews/CameraViewEntry'
import { MeasurementEntry } from 'features/kestrelViewer/kestrelViewerMarkup/measurements/MeasurementEntry'
import { PlaneIntersectionEntry } from 'features/kestrelViewer/kestrelViewerMarkup/planeIntersections/PlaneIntersectionEntry'
import { PolygonEntry } from 'features/kestrelViewer/kestrelViewerMarkup/polygons/PolygonEntry'
import { SurfaceAreaEntry } from 'features/kestrelViewer/kestrelViewerMarkup/surfaceAreas/SurfaceAreaEntry'
import React, { FC } from 'react'
import styled from 'styled-components'
import {
  KVAnnotation,
  KVCameraView,
  KVMeasurement,
  KVPlaneIntersection,
  KVPolygon,
  KVSurfaceArea,
} from 'types'

const Section = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 12px;
`

const EntriesList = styled(Toggle.Root)`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const SectionTitle = styled.span`
  font-size: 1rem;
  display: block;
  position: sticky;
  top: 0;
  color: ${({ theme }) => theme.forecast.text1};
  height: min-content;
  font-weight: bold;
`

export const KestrelViewerMarkupSection: FC = () => {
  const {
    selectedMarkup,
    annotations,
    polygons,
    measurements,
    planeIntersections,
    surfaceAreas,
    cameraViews,
  } = useKestrel()

  return (
    <>
      {!!cameraViews && (
        <Section>
          <SectionTitle>Camera Views</SectionTitle>
          <EntriesList
            type='single'
            aria-label='Select camera view'
            onClick={(event) => {
              event.stopPropagation()
            }}>
            {cameraViews.map((cv: KVCameraView) => (
              <CameraViewEntry
                key={cv.id}
                cameraView={cv}
                selectedMarkupId={selectedMarkup?.id}
              />
            ))}
          </EntriesList>
        </Section>
      )}

      {!!polygons && (
        <Section>
          <SectionTitle>Inspection Areas</SectionTitle>
          <EntriesList
            type='single'
            aria-label='Select inspection area'
            onClick={(event) => {
              event.stopPropagation()
            }}>
            {polygons.map((p: KVPolygon) => (
              <PolygonEntry
                key={p.id}
                polygon={p}
                selectedMarkupId={selectedMarkup?.id}
              />
            ))}
          </EntriesList>
        </Section>
      )}

      {!!annotations && (
        <Section>
          <SectionTitle>Annotations</SectionTitle>
          <EntriesList
            type='single'
            aria-label='Select annotation'
            onClick={(event) => {
              event.stopPropagation()
            }}>
            {annotations.map((a: KVAnnotation) => (
              <AnnotationEntry
                key={a.id}
                annotation={a}
                selectedMarkupId={selectedMarkup?.id}
              />
            ))}
          </EntriesList>
        </Section>
      )}

      {!!measurements && (
        <Section>
          <SectionTitle>Measurements</SectionTitle>
          <EntriesList
            type='single'
            aria-label='Select inspection area'
            onClick={(event) => {
              event.stopPropagation()
            }}>
            {measurements.map((m: KVMeasurement) => (
              <MeasurementEntry
                key={m.id}
                measurement={m}
                selectedMarkupId={selectedMarkup?.id}
              />
            ))}
          </EntriesList>
        </Section>
      )}

      {!!surfaceAreas && (
        <Section>
          <SectionTitle>Surface Areas</SectionTitle>
          <EntriesList
            type='single'
            aria-label='Select surface area'
            onClick={(event) => {
              event.stopPropagation()
            }}>
            {surfaceAreas.map((s: KVSurfaceArea) => (
              <SurfaceAreaEntry
                key={s.surfaceAreaId}
                surfaceArea={s}
                selectedMarkupId={selectedMarkup?.id}
              />
            ))}
          </EntriesList>
        </Section>
      )}

      {!!planeIntersections && (
        <Section>
          <SectionTitle>Plane Intersections</SectionTitle>
          <EntriesList
            type='single'
            aria-label='Select plane intersection'
            onClick={(event) => {
              event.stopPropagation()
            }}>
            {planeIntersections.map((p: KVPlaneIntersection) => (
              <PlaneIntersectionEntry
                key={p.id}
                planeIntersection={p}
                selectedMarkupId={selectedMarkup?.id}
              />
            ))}
          </EntriesList>
        </Section>
      )}
    </>
  )
}
